import type { KeyboardEvent, ReactNode } from 'react';
import { cardClasses, type CardDensity, type CardTone } from './Card.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';
import { Metric } from './Metric.js';
import type { ChartColor } from './chart.js';

/*
 * O vocabulário de tom é o do `Card` — um nome por conceito. O que muda é o que
 * cada tom PINTA: no cartão é a borda e o fundo do bloco inteiro, aqui é a
 * pastilha de 36px. Um segundo tipo com os mesmos cinco valores seria a porta
 * por onde eles passariam a divergir.
 */
const TILES: Record<CardTone, string> = {
  neutro: 'bg-superficie-suave text-texto-suave',
  primaria: 'bg-primaria-suave text-primaria-texto',
  sucesso: 'bg-sucesso-suave text-sucesso',
  alerta: 'bg-alerta-suave text-alerta',
  erro: 'bg-erro-suave text-erro',
};

/**
 * O tom DECORATIVO de cada símbolo — o assunto do cartão, nunca o estado dele.
 *
 * Entrou em 18/09/2026 por decisão do dono: a fileira de cartões era cinza, e
 * ele pediu cor nos símbolos — **não cor forte, e sim a cor semântica com
 * transparência**. Este mapa é o que torna esse pedido um padrão em vez de uma
 * escolha por tela: o tom sai do ÍCONE, que já é obrigatório, então o mesmo
 * símbolo tem a mesma cor em toda a casa sem ninguém precisar lembrar.
 *
 * ## O que separa decoração de afirmação é a SATURAÇÃO, e é só isso
 *
 * O docblock abaixo dizia que cor por assunto "gasta em decoração a cor que o
 * resto do produto usa para estado", e continua certo sobre cor CHEIA: numa
 * tela onde `Badge` pinta de vermelho o que está vencido, um cartão vermelho
 * porque "o assunto é boleto" faz as duas afirmações competirem.
 *
 * A transparência é o que desfaz o empate. O símbolo decorativo desenha a 60%
 * — 40% de transparência, o extremo mais legível da faixa que o dono pediu —,
 * e o símbolo de estado desenha inteiro, **junto com o número pintado** pelo
 * `Metric`. São dois sinais de diferença, não um.
 *
 * ## Por que só DOIS tons aqui, e não os quatro
 *
 * Medido na paleta: na interna `--cor-primaria` é o laranja da marca (hue 44) e
 * `--cor-alerta` é o âmbar (hue 73). São vizinhos — lado a lado numa fileira de
 * quatro cartões eles não se distinguem, e usar os dois com significados
 * diferentes seria pedir que alguém lesse a diferença entre dois laranjas.
 *
 * Então o âmbar e o vermelho **ficam fora**, reservados para `tone`. É a metade
 * da regra antiga que sobrevive inteira: as duas cores com que o produto diz
 * "algo está errado" nunca aparecem como enfeite.
 *
 * ## Quem não está aqui fica cinza, e isso é escolha
 *
 * `relogio`, `atencao` e `seta-cima` são os símbolos que quase sempre chegam
 * COM `tone` — prazo vencido, entidade que nunca sincronizou, dinheiro que
 * saiu. Dar a eles um tom decorativo seria pintá-los duas vezes com duas
 * intenções, e a fileira perderia o cinza que faz a cor dos outros valer.
 *
 * As chaves são os VALORES de `IconName`, e não nomes nossos — o mapa é
 * indexado pela união literal, como o `STROKES` de `Icon.tsx`. Daí a marca: o
 * vocabulário de ícone é em português por ser dado, e traduzi-lo quebraria a
 * indexação.
 */
// @valor
const ACCENTS: Partial<Record<IconName, CardTone>> = {
  /* o que é dinheiro, conferência ou entrada — verde */
  dinheiro: 'sucesso',
  confirmado: 'sucesso',
  carrinho: 'sucesso',
  percentual: 'sucesso',
  'seta-baixo': 'sucesso',

  /* o assunto do módulo — o laranja da marca */
  pessoa: 'primaria',
  pessoas: 'primaria',
  demandas: 'primaria',
  retencao: 'primaria',
  cobranca: 'primaria',
  comercial: 'primaria',
  veiculo: 'primaria',
  erp: 'primaria',
  plataforma: 'primaria',
  gestao: 'primaria',
  quadro: 'primaria',
  livro: 'primaria',
  cracha: 'primaria',
  grafico: 'primaria',
};

/**
 * A pastilha de 36px com o símbolo — a do `StatCard`, e a de quem desenha a
 * mesma anatomia DENTRO de um cartão (a "Visão comercial" e a grade de módulos
 * do início, desde 29/09/2026), onde um `StatCard` poria cartão dentro de
 * cartão.
 *
 * Saiu de dentro do `StatCard` para o tom do símbolo continuar tendo UM dono:
 * copiada numa tela, a pastilha passaria a escolher cor por conta própria, e o
 * mesmo ícone sairia laranja num lugar e cinza no outro.
 */
// @valor
const LEGENDS: Record<ChartColor, string> = {
  'fase-laranja': 'bg-fase-laranja-suave text-fase-laranja',
  'fase-azul': 'bg-fase-azul-suave text-fase-azul',
  'fase-rosa': 'bg-fase-rosa-suave text-fase-rosa',
  'fase-amarelo': 'bg-fase-amarelo-suave text-fase-amarelo',
  'fase-roxo': 'bg-fase-roxo-suave text-fase-roxo',
  'fase-turquesa': 'bg-fase-turquesa-suave text-fase-turquesa',
  'fase-verde': 'bg-fase-verde-suave text-fase-verde',
  'fase-vermelho': 'bg-fase-vermelho-suave text-fase-vermelho',
  'fase-cinza': 'bg-fase-cinza-suave text-fase-cinza',
};

export function IconTile({
  icon,
  tone = 'neutro',
  legend,
}: {
  icon: IconName;
  tone?: CardTone;
  /** A cor da série do gráfico que o cartão totaliza. Ver `StatCard.legend`. */
  legend?: ChartColor;
}) {
  if (legend !== undefined) {
    return (
      <span
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-borda',
          LEGENDS[legend],
        )}
      >
        <Icon nome={icon} />
      </span>
    );
  }

  /*
   * O tom do estado GANHA do tom decorativo, sempre.
   *
   * Um cartão não pode dizer duas coisas com cor: se o número é a má notícia,
   * a pastilha é dela. O tom decorativo só aparece quando `tone` é `neutro` —
   * isto é, quando não há afirmação nenhuma a fazer.
   */
  const decorative = tone === 'neutro' ? (ACCENTS[icon] ?? 'neutro') : tone;
  const tinted = tone === 'neutro' && decorative !== 'neutro';

  return (
    <span
      className={cn(
        'grid size-9 shrink-0 place-items-center rounded-borda',
        TILES[decorative],
      )}
    >
      {/*
       * Sem `label`: o ícone aqui é decoração do rótulo que está a três pixels
       * dele, e anunciá-lo faria o leitor de tela ler o assunto duas vezes. A
       * regra do `DESIGN.md` é "ícone nunca vai sozinho quando carrega
       * informação" — este não vai, e não carrega.
       */}
      {/*
       * A transparência mora no SÍMBOLO, e não na pastilha.
       *
       * Fadiando a pastilha inteira, o fundo `-suave` — que já está perto do
       * branco — desapareceria, e o cartão perderia a forma que o alinha com os
       * vizinhos. Fadiando só o glifo, a pastilha continua sendo um bloco e a
       * cor dele para de competir com o `Badge` da mesma tela.
       */}
      <Icon nome={icon} className={cn(tinted && 'opacity-60')} />
    </span>
  );
}

/**
 * O número como cartão: pastilha do ícone, rótulo, valor e um rodapé opcional.
 *
 * ## O defeito que isto conserta
 *
 * `Metric` desenha `<dt>`/`<dd>` nus dentro de uma grade, e é a peça certa
 * DENTRO de um painel — cartão dentro de cartão está sempre errado. O que não
 * existia era a forma de fora: o número que é um bloco por si, na página.
 *
 * Quem precisava dele escrevia uma grade de `Metric` solta entre o título da
 * tela e a tabela, e o resultado desalinhava de duas maneiras que ninguém
 * revisava:
 *
 * - **altura.** Uma métrica com linha de apoio é mais alta que a vizinha sem, e
 *   sem caixa em volta a diferença aparece como três números pendurados em
 *   alturas diferentes. Aqui a grade estica as caixas, e o rodapé é empurrado
 *   para baixo por `mt-auto` — as caixas de uma fileira fecham juntas;
 * - **plano.** Sem superfície própria, os números ficavam no mesmo plano do
 *   título e da tabela, e a tela lia como três blocos soltos numa folha. É o
 *   mesmo defeito que `FilterCard` resolveu para os filtros em 06/09.
 *
 * ## O ícone é obrigatório, e isso é sobre alinhamento
 *
 * Uma fileira em que metade dos cartões tem pastilha e a outra metade não é a
 * mesma desigualdade com outro nome: o rótulo dos sem-ícone começa 44px à
 * esquerda do rótulo dos com. Exigir o ícone torna a fileira alinhada por
 * construção, e não por quem escreveu a tela ter lembrado.
 *
 * ## A cor da pastilha é SEMÂNTICA, e o padrão é não dizer nada
 *
 * `tone` só quando o próprio número é a notícia — vencidos em `erro`, pendente
 * em `alerta`. Sem ele a pastilha é cinza. A referência de estilo trazia uma
 * cor por assunto (verde para pessoas, laranja para boletos), e isso gasta em
 * decoração a cor que o resto do produto usa para estado: numa tela onde
 * `Badge` pinta de vermelho o que está vencido, um cartão vermelho porque "o
 * assunto é boleto" faz as duas afirmações competirem.
 *
 * `tone` é repassado ao `Metric`, então o `erro` pinta o número junto — quem
 * pinta a pastilha de vermelho está dizendo que o número é a má notícia, e o
 * número precisa dizer o mesmo.
 *
 * ## O que ele NÃO é
 *
 * O `DESIGN.md` bane o painel de métrica gigante — número enorme, rótulo
 * pequeno, acento em gradiente — e a grade de cartões idênticos com ícone,
 * título e parágrafo. Esta peça nasceu por decisão do dono em 17/09/2026, com
 * referência de desenho, e fica **dentro** daquela regra em vez de a revogar:
 * o valor é o `text-xl` do `Metric`, que é a escala de número desta casa em
 * todo painel das duas SPAs; não há gradiente, não há parágrafo e o cartão
 * carrega um dado medido, nunca um enfeite. Ver a exceção registrada no
 * `DESIGN.md`.
 *
 * E o esqueleto continua sendo do `Metric`: `value === undefined` é "ainda não
 * sei", e nunca um zero. Zero é um fato, e afirmá-lo antes de saber é a tela
 * mentindo com aparência de precisão.
 */

export function StatCard({
  icon,
  label,
  value,
  support,
  footer,
  tone = 'neutro',
  legend,
  density = 'normal',
  className,
  onClick,
  pressed,
}: {
  /** A pastilha. Obrigatória: ver o cabeçalho. */
  icon: IconName;
  label: string;
  /** `undefined` é "ainda não sei", e vira esqueleto. Nunca passe zero por ele. */
  value: ReactNode | undefined;
  /** A linha de apoio abaixo do número — "3 boletos", "de 12 no mês". */
  support?: ReactNode;
  /**
   * Os fatos secundários, abaixo de uma linha. Ticket médio, quebra por
   * situação — o que qualifica o número sem competir com ele.
   */
  footer?: ReactNode;
  /** Só quando o próprio número é a notícia. Ver o cabeçalho. */
  tone?: CardTone;
  /**
   * O cartão é o TOTAL de uma série de gráfico, e a pastilha veste a cor dela
   * — o "Deixa de ganhar" laranja embaixo das colunas laranja (28/10/2026,
   * pedido do dono). Não é a cor decorativa que o cabeçalho recusa: é a
   * legenda, e liga o número à série que ele soma. Vence `tone` na pastilha.
   */
  legend?: ChartColor;
  density?: CardDensity;
  className?: string;
  /**
   * O cartão que FAZ algo na tela — o "Não pagos" da ficha do carro, que
   * destaca as linhas na tabela de boletos (22/10/2026). Com ele, o cartão
   * vira um alvo de clique e de teclado (Enter e Espaço), e `pressed` diz se
   * o efeito está ligado.
   *
   * É `role="button"` num `<div>`, e não um `<button>`, porque o par
   * `<dl>`/`<dt>`/`<dd>` do `Metric` não é conteúdo que um `<button>` aceite.
   */
  onClick?: () => void;
  pressed?: boolean;
}) {
  return (
    <div
      {...(onClick && {
        role: 'button',
        tabIndex: 0,
        'aria-pressed': pressed ?? false,
        onClick,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onClick();
        },
      })}
      className={cardClasses({
        density,
        /*
         * `h-full` porque a grade estica a célula e não o conteúdo dela: sem
         * isto, o cartão de rodapé mais curto fica mais baixo que o vizinho e a
         * fileira volta a desalinhar — que é o defeito que esta peça existe
         * para fechar.
         */
        className: cn(
          'flex h-full flex-col',
          onClick &&
            'cursor-pointer transition-colors hover:border-primaria aria-pressed:border-primaria aria-pressed:bg-primaria-suave',
          className,
        ),
      })}
    >
      <div className="flex items-center gap-3">
        <IconTile icon={icon} tone={tone} legend={legend} />

        {/*
         * `<dl>` é obrigatório: `Metric` renderiza `<dt>`/`<dd>`, que fora de um
         * `<dl>` não formam par nenhum para quem lê por leitor de tela. Um por
         * cartão, e não um em volta da grade — o `<div>` do cartão entre a lista
         * e o par quebraria a associação.
         */}
        <dl className="min-w-0 flex-1">
          <Metric
            label={label}
            value={value}
            support={support}
            tone={tone === 'erro' ? 'erro' : undefined}
          />
        </dl>
      </div>

      {footer !== undefined && (
        /*
         * `mt-auto` e não `mt-3`: o rodapé encosta no pé do cartão, e é isso que
         * faz a linha dele ficar na mesma altura em todos os cartões da fileira
         * mesmo quando um deles tem linha de apoio e o outro não.
         *
         * `borda-suave`: a separação é DENTRO do cartão, e a borda de contorno
         * repetida aqui desenharia um segundo cartão lá dentro.
         */
        <div className="mt-auto pt-3.5">
          <div className="border-t border-borda-suave pt-3 text-xs text-texto-suave">
            {footer}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A grade em que os cartões de número ficam.
 *
 * Existe porque quem escrevia a tela dizia `grid grid-cols-2 gap-3
 * sm:grid-cols-4` à mão, e a terceira cópia já divergia no `gap`. Ela substitui
 * a `MetricList`, que era a mesma grade sem os cartões dentro.
 *
 * ## `auto-fill`, e por isso ela NÃO tem `columns`
 *
 * A primeira versão pedia o número de colunas a quem escrevia a tela — 4 nas
 * demandas, 2 no ERP, 3 na cobrança —, e medido no navegador em 17/09/2026 a
 * 1440px o resultado foi: cartão de 270px no primeiro painel, 550px no segundo
 * e 366px no terceiro, **empilhados na mesma tela**. Cada painel estava certo
 * isolado; o conjunto é que não era, que é o defeito que esta leva inteira
 * existe para fechar.
 *
 * `auto-fill` inverte quem decide: a trilha tem um tamanho mínimo, o navegador
 * cria quantas couberem e **mantém as vazias**, então o cartão tem a mesma
 * largura em todo painel da página, com qualquer número deles. É o oposto do
 * `auto-fit` do `FilterCard` — lá as trilhas vazias colapsam de propósito,
 * porque um campo de filtro sozinho deve ocupar a linha; aqui colapsar é
 * justamente o que produziria as três larguras.
 *
 * 15rem é o mínimo em que "R$ 199.999,00" cabe ao lado da pastilha de 36px sem
 * quebrar. Abaixo disso a grade cai para uma coluna — e é uma, não duas: a
 * 390px dois cartões lado a lado cortam o valor, que é o único conteúdo pelo
 * qual alguém abriu a tela.
 */
export function StatCardList({
  children,
  className,
  stretch = false,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Os cartões ESTICAM até a largura do contêiner (`auto-fit`), em vez de
   * guardar as trilhas vazias.
   *
   * Para a tela em que a grade divide a coluna com blocos de largura cheia —
   * a Carteira, desde 19/10/2026: três números de 15rem ao lado de um cartão
   * de 1100px deixavam um vão à direita que o dono apontou. Ali o que alinha é
   * a borda do bloco vizinho, e não a largura do cartão de outra página.
   * O mínimo continua 15rem, e o telefone continua com uma coluna.
   */
  stretch?: boolean;
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-3',
        stretch
          ? 'sm:[grid-template-columns:repeat(auto-fit,minmax(15rem,1fr))]'
          : 'sm:[grid-template-columns:repeat(auto-fill,minmax(15rem,1fr))]',
        className,
      )}
    >
      {children}
    </div>
  );
}
